import {
  CommonHelperFunctions,
  GetUserByIdModel,
  ITicket,
  SelectedTicketModel,
  SendCommentModel,
  ZendeskTicketComment
} from "@kuika/kuika-cl-model";
import {
  IZendeskGetSelectedTicketResponse,
  IZendeskGetTicketsResponse,
  IZendeskGetUsersPhotosResponse
} from "@kuika/kuika-cl-model/src/models/zendesk";
import Axios, { AxiosResponse } from "axios";
import { KMainFunctions } from "../shared/utility/main-functions";

const backendUrl = CommonHelperFunctions.GetBackendUrl();

class ZendeskService {
  public static async GetTickets(model: ITicket): Promise<AxiosResponse<IZendeskGetTicketsResponse>> {
    try {
      return await Axios.post<ITicket, AxiosResponse<IZendeskGetTicketsResponse>>(
        `${backendUrl}/ZendeskAPI/ZendeskGetTickets`,
        model
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  /**
   * Fetches a single ticket by id. Unlike the other Zendesk calls, this one does
   * NOT swallow the error and return it as if it were a response. A freshly-created
   * ticket can briefly 404 on the single-ticket endpoint while it propagates from
   * the list feed; the caller needs the rejection so it can tell that apart from a
   * real ticket and degrade gracefully instead of crashing on `res.data.ticket`
   * (KOA-17780). Telemetry is handled by the global Axios response interceptor.
   */
  public static GetSelectedTicket(
    model: SelectedTicketModel
  ): Promise<AxiosResponse<IZendeskGetSelectedTicketResponse>> {
    return Axios.post<SelectedTicketModel, AxiosResponse<IZendeskGetSelectedTicketResponse>>(
      `${backendUrl}/ZendeskAPI/ZendeskGetSelectedTicket`,
      model
    );
  }

  public static async GetUserById(model: GetUserByIdModel): Promise<AxiosResponse> {
    try {
      return await Axios.post<GetUserByIdModel, AxiosResponse>(`${backendUrl}/ZendeskAPI/ZendeskGetUserById`, model);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetSelectedTicketComments(
    model: SelectedTicketModel
  ): Promise<AxiosResponse<ZendeskTicketComment[]>> {
    try {
      return await Axios.post<SelectedTicketModel, AxiosResponse<ZendeskTicketComment[]>>(
        `${backendUrl}/ZendeskAPI/ZendeskGetTicketComments`,
        model
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async SendComment(model: SendCommentModel): Promise<AxiosResponse> {
    try {
      return await Axios.post<SendCommentModel, AxiosResponse>(`${backendUrl}/ZendeskAPI/ZendeskSendComment`, model);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async UpdateTicketStatus(id: string): Promise<AxiosResponse> {
    try {
      return await Axios.post<SelectedTicketModel, AxiosResponse>(
        `${backendUrl}/ZendeskAPI/ZendeskUpdateTicketStatus`,
        {
          id: id.toString()
        }
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetUsersPhotos(
    model: GetUserByIdModel[]
  ): Promise<AxiosResponse<IZendeskGetUsersPhotosResponse>> {
    try {
      return await Axios.post<GetUserByIdModel[], AxiosResponse<IZendeskGetUsersPhotosResponse>>(
        `${backendUrl}/ZendeskAPI/ZendeskGetUsersPhotos`,
        model
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetUserTicketNotifyCount(): Promise<AxiosResponse> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/ZendeskAPI/ZendeskGetUserTicketNotifyCount`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }
}

export default ZendeskService;
